// comunicacoesSection/index.ts
//
// Folha única da aba "Comunicações" — as quatro sub-telas (Canais, Templates,
// Automações, Log de envio) compartilham o mesmo kit, do jeito que
// storesSection/MeiosPagamento.ts faz para Lojas.
//
// Tamanho só na escada de tokens: `var(--sp-N)` (2,4,5,6,8,10,12,15,16,20,24,
// 32,40) e `var(--fs-N)`; o que não cai na escada vira `calc(var(--u) * N)`.
// Borda de 1px, raio e sombra continuam fixos — ver o cabeçalho de
// styles/global/escala.ts, que os declara fora do assunto da escala.
//
// Cores só de `props.theme.colors`, que hoje tem cinco chaves de verdade
// (background, text, selection, modalColor, borderColor). `colors.border`,
// `colors.label` e `colors.danger` resolvem undefined em todo o repo; aqui a
// dívida é herdada, não consertada — por isso todo uso vem com fallback.
import styled, { keyframes } from "styled-components";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-24);
  gap: var(--sp-16);
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
`;

export const HeaderContent = styled.div`
  flex: 1;
  min-width: calc(var(--u) * 260);
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  margin: 0 0 var(--sp-6) 0;
  font-size: var(--fs-3xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

// ===== CARTÃO (uma loja, um template, uma automação) =====
export const Cartao = styled.div`
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 12px;
  padding: var(--sp-20);
  margin-bottom: var(--sp-20);
  animation: ${fadeIn} 0.3s ease;
`;

export const CartaoTitulo = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const CartaoSubtitulo = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  margin-top: var(--sp-4);
  margin-bottom: var(--sp-16);
`;

// ===== BLOCOS INTERNOS =====
export const BlocosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 300), 1fr));
  gap: var(--sp-16);
`;

export const Bloco = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  border-radius: 8px;
  padding: var(--sp-16);
`;

export const BlocoTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-md);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-12);
`;

export const Linha = styled.label<{ $desabilitada?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-6) 0;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
  cursor: ${(props) => (props.$desabilitada ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$desabilitada ? 0.5 : 1)};
`;

export const Caixa = styled.input`
  width: var(--sp-16);
  height: var(--sp-16);
  cursor: inherit;
  accent-color: ${(props) => props.theme.colors.selection};
`;

/** Corpo recuado de um bloco, no molde do "LinhaMeios" de Meios de Pagamento. */
export const Recuo = styled.div<{ $desabilitada?: boolean }>`
  padding-left: var(--sp-24);
  border-left: 2px solid ${(props) => props.theme.colors.selection + "30"};
  margin-left: var(--sp-6);
  opacity: ${(props) => (props.$desabilitada ? 0.45 : 1)};
`;

// ===== CAMPOS =====
export const CampoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 200), 1fr));
  gap: var(--sp-12);
  margin-top: var(--sp-8);
`;

export const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
`;

export const RotuloCampo = styled.span`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

/** Explicação de uma linha embaixo do campo — onde mora o aviso da credencial. */
export const AjudaCampo = styled.span`
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.label};
  line-height: 1.4;
`;

export const Entrada = styled.input`
  padding: var(--sp-8) var(--sp-10);
  border-radius: 6px;
  border: 1px solid
    ${(props) => props.theme.colors.borderColor || props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.modalColor};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  width: 100%;
  box-sizing: border-box;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Seletor = styled.select`
  padding: var(--sp-6) var(--sp-10);
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.modalColor};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// ===== RODAPÉ DE AÇÃO =====
export const AcaoRodape = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  flex-wrap: wrap;
  margin-top: var(--sp-16);
  padding-top: var(--sp-16);
  border-top: 1px solid ${(props) => props.theme.colors.selection + "30"};
`;

export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-10) var(--sp-20);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-6);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }
`;

export const SecondaryButton = styled(Button)`
  background-color: transparent;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  color: ${(props) => props.theme.colors.text};
`;

// ===== AVISO INLINE =====
// Vive na própria aba, e não no <MessageBox /> do Zustand: o MessageBox só está
// montado em Login e Register, então `setMessageZustand` dentro da Configuração
// grava uma mensagem que ninguém vê.
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  margin-top: var(--sp-12);
  padding: var(--sp-10) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.4;
  border: 1px solid
    ${(props) =>
      props.$tom === "erro"
        ? "#be3028"
        : props.$tom === "atencao"
        ? "#8f5e07"
        : "#26792f"};
  background-color: ${(props) =>
    props.$tom === "erro"
      ? "#be302818"
      : props.$tom === "atencao"
      ? "#8f5e0718"
      : "#26792f18"};
  color: ${(props) => props.theme.colors.text};
  animation: ${fadeIn} 0.3s ease;
`;

/** Mesmo desenho do Aviso, mas no topo da tela (antes de qualquer digitação). */
export const AvisoTopo = styled(Aviso)`
  margin-top: 0;
  margin-bottom: var(--sp-20);
`;

// ===== LISTA (templates gravados, automações gravadas) =====
export const Lista = styled.div`
  display: grid;
  gap: var(--sp-12);
`;

export const ListaItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--sp-16);
  padding: var(--sp-16);
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  border-radius: 8px;
`;

export const ListaItemCorpo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ListaItemTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  flex-wrap: wrap;
  font-weight: 600;
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-6);
`;

export const ListaItemTexto = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  line-height: 1.4;
`;

export const Etiqueta = styled.span<{ $tom?: "neutro" | "semente" | "gravado" }>`
  padding: var(--sp-2) var(--sp-8);
  border-radius: 12px;
  font-size: var(--fs-2xs);
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid
    ${(props) =>
      props.$tom === "gravado"
        ? "#26792f"
        : props.$tom === "semente"
        ? "#8f5e07"
        : props.theme.colors.selection + "60"};
  color: ${(props) => props.theme.colors.text};
  background-color: ${(props) =>
    props.$tom === "gravado"
      ? "#26792f18"
      : props.$tom === "semente"
      ? "#8f5e0718"
      : "transparent"};
`;

/** Conteúdo do template: monoespaçado, quebras preservadas, sem virar HTML. */
export const Trecho = styled.pre`
  margin: 0 0 var(--sp-12) 0;
  padding: var(--sp-12);
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  border-radius: 6px;
  font-family: monospace;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const Variaveis = styled.div`
  display: flex;
  gap: var(--sp-8);
  flex-wrap: wrap;
  align-items: center;
`;

export const Variavel = styled.code`
  padding: var(--sp-2) var(--sp-6);
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.selection + "25"};
  color: ${(props) => props.theme.colors.text};
  font-family: monospace;
  font-size: var(--fs-2xs);
`;

// ===== ESTADOS =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-16);
  padding: calc(var(--u) * 60);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: var(--sp-40);
  height: var(--sp-40);
  border: 4px solid ${(props) => props.theme.colors.selection + "30"};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

export const EmptyState = styled.div`
  border: 2px dashed ${(props) => props.theme.colors.selection + "50"};
  border-radius: 12px;
  padding: calc(var(--u) * 60) var(--sp-40);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-8);
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
  line-height: 1.5;
`;

// ===== EXPLICAÇÃO DE TELA QUE NÃO EXISTE (Log de envio) =====
// Não é EmptyState: vazio dá a entender "ainda não aconteceu nada". Aqui o
// caso é outro — a entidade não existe, então não há o que carregar nunca.
export const Motivo = styled.div`
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 12px;
  padding: var(--sp-24);
  animation: ${fadeIn} 0.3s ease;
`;

export const MotivoTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-12);
`;

export const MotivoTexto = styled.p`
  margin: 0 0 var(--sp-12) 0;
  font-size: var(--fs-base);
  line-height: 1.6;
  color: ${(props) => props.theme.colors.label};
`;

export const MotivoLista = styled.ul`
  margin: 0;
  padding-left: var(--sp-24);
  font-size: var(--fs-md);
  line-height: 1.6;
  color: ${(props) => props.theme.colors.label};
`;

export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;
